import React, { useState, useRef, useEffect } from 'react';
import { Send, Zap, Mic, ArrowLeft, AlertTriangle, TrendingUp, Activity, Brain, Calendar, Users, BookOpen, GraduationCap, Scale, Paperclip, ArrowRight, Shield, UserCheck } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { ORG_HEALTH_DATA, ORG_HEALTH_CHAT_RESPONSES } from '../../data/orgHealthData';

const MetricCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = item.alert ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = item.alert ? (isDark ? 'border-red-500/15' : 'border-red-200/50') : border;
  return (
    <div className={`${cardBg} border ${cardBorder} rounded-xl px-3 py-2.5 text-center`}>
      <p className={`text-[9px] ${ts}`}>{item.label}</p>
      <p className={`text-base font-bold mt-0.5 ${item.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{item.value}</p>
      <span className={`text-[9px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
    </div>
  );
};

const TurnoverCard = ({ person, isDark, onAction }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const probColor = person.probability >= 60 ? (isDark ? 'text-red-400' : 'text-red-600') : person.probability >= 40 ? (isDark ? 'text-amber-400' : 'text-amber-600') : (isDark ? 'text-yellow-400' : 'text-yellow-600');
  const probBg = person.probability >= 60 ? (isDark ? 'bg-red-500/10' : 'bg-red-50') : person.probability >= 40 ? (isDark ? 'bg-amber-500/10' : 'bg-amber-50') : (isDark ? 'bg-yellow-500/10' : 'bg-yellow-50');
  const cardBg = person.probability >= 60 ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : person.probability >= 40 ? (isDark ? 'bg-amber-950/10' : 'bg-amber-50/30') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBorder = person.probability >= 60 ? (isDark ? 'border-red-500/15' : 'border-red-200/50') : person.probability >= 40 ? (isDark ? 'border-amber-500/10' : 'border-amber-200/40') : border;
  const ctaLabel = person.probability >= 60 ? 'Intervene Now' : person.probability >= 40 ? 'Create Plan' : 'Monitor';

  return (
    <div className={`${cardBg} border ${cardBorder} rounded-xl p-3`} data-testid={`turnover-${person.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <p className={`text-[11px] font-semibold ${tp}`}>{person.name}</p>
          <p className={`text-[9px] ${ts}`}>{person.role} · {person.department}</p>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${probBg} ${probColor}`}>{person.probability}%</span>
      </div>
      <div className="flex items-center gap-2 mb-1.5">
        <Calendar className={`w-3 h-3 ${ts}`} />
        <span className={`text-[9px] ${ts}`}>Window: {person.window}</span>
      </div>
      <div className="flex flex-wrap gap-1 mb-2">
        {person.signals.map((s, i) => (
          <span key={i} className={`text-[8px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-white/[0.04] text-white/50' : 'bg-black/[0.03] text-slate-500'}`}>{s}</span>
        ))}
      </div>
      <div className="pt-2 border-t border-dashed" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
        <div className="flex items-center gap-1.5 mb-1">
          <Brain className={`w-3 h-3 ${isDark ? 'text-indigo-400' : 'text-indigo-500'}`} />
          <span className={`text-[9px] font-semibold ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>Prediction</span>
        </div>
        <p className={`text-[9px] leading-relaxed ${ts}`}>{person.reasoning}</p>
      </div>
      <div className="mt-2 pt-2 border-t border-dashed" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
        <button onClick={() => onAction?.(ctaLabel, person.name)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors ${person.probability >= 60 ? (isDark ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-red-50 text-red-600 hover:bg-red-100') : (isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100')}`}>
          <UserCheck className="w-3 h-3" /> {ctaLabel}
        </button>
      </div>
    </div>
  );
};

const DualInsightCard = ({ item, isDark, agent, agentKey }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const typeBg = { recommendation: isDark ? 'bg-indigo-500/8' : 'bg-indigo-50/40', alert: isDark ? 'bg-red-500/5' : 'bg-red-50/30', insight: isDark ? 'bg-emerald-500/5' : 'bg-emerald-50/30' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border ${border} ${typeBg[item.type] || (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]')}`}>
      <img src={AGENT_THUMBS[agentKey]} alt="" className="w-4 h-4 rounded-full object-cover flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          <Icon className={`w-3 h-3 ${colors[item.type]}`} />
          <span className={`text-[8px] font-semibold uppercase tracking-wide ${colors[item.type]}`}>{item.type}</span>
        </div>
        <p className={`text-[10px] leading-relaxed ${tp}`}>{item.text}</p>
        <p className={`text-[8px] mt-1.5 ${ts}`}>{agent} · {item.time}</p>
      </div>
    </div>
  );
};

const V4PeopleOrgHealth = ({ isDark, onWorkflowToggle, workflowPanelOpen }) => {
  const [activeAgent, setActiveAgent] = useState('tali');
  const [message, setMessage] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const sendMessage = (text, resp) => {
    const agentResp = resp || ORG_HEALTH_CHAT_RESPONSES[activeAgent];
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: agentResp.text, agent: activeAgent }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };
  const handleCta = (label, detail) => {
    sendMessage(`${label} — ${detail}`, { text: `I'll handle "${label}" for ${detail}. Setting that up now and will send you a confirmation.\n\nAnything else you need?` });
  };

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder={`Message ${activeAgent === 'tali' ? 'Tali' : 'Dora'} about org health...`} rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto', maxHeight: '200px' }} data-testid="orghealth-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <button onClick={() => setActiveAgent('tali')}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${activeAgent === 'tali' ? (isDark ? 'bg-white/[0.08] ring-1 ring-indigo-500/30' : 'bg-black/[0.06] ring-1 ring-indigo-500/30') : (isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]')}`}
                data-testid="orghealth-agent-tali"
              >
                <img src={AGENT_THUMBS.tali} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Tali</span>
              </button>
              <button onClick={() => setActiveAgent('dora')}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${activeAgent === 'dora' ? (isDark ? 'bg-white/[0.08] ring-1 ring-indigo-500/30' : 'bg-black/[0.06] ring-1 ring-indigo-500/30') : (isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]')}`}
                data-testid="orghealth-agent-dora"
              >
                <img src={AGENT_THUMBS.dora} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Dora</span>
              </button>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="orghealth-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="orghealth-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid="orghealth-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Org Health</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS[msg.agent || activeAgent]} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  const d = ORG_HEALTH_DATA;
  return (
    <div className="flex flex-col h-full" data-testid="orghealth-page">
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Org Health</p>
          </div>
          <button onClick={() => handleCta('Run D&I Report', 'Generate full diversity and inclusion analysis')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="orghealth-report-btn">
            <Shield className="w-3.5 h-3.5" /> Run D&I Report
          </button>
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
        <div className="grid grid-cols-6 gap-3">
          {d.metrics.map((m, i) => <MetricCard key={i} item={m} isDark={isDark} />)}
        </div>

        {/* Two columns: Diversity + Compensation */}
        <div className="grid grid-cols-2 gap-4">
          {/* Diversity */}
          <div className={`${cardBg} border ${border} rounded-xl p-4`} data-testid="diversity-section">
            <div className="flex items-center gap-2 mb-3">
              <Users className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-[11px] font-semibold ${tp}`}>Diversity & Inclusion</p>
            </div>
            {/* Gender bar */}
            <div className="mb-4">
              <p className={`text-[9px] mb-1.5 ${ts}`}>Gender Distribution</p>
              <div className="flex h-5 rounded-full overflow-hidden gap-[1px]">
                <div className="h-full bg-indigo-500 rounded-l-full" style={{ width: `${d.diversity.gender.female}%` }} />
                <div className="h-full bg-emerald-500" style={{ width: `${d.diversity.gender.nonBinary}%` }} />
                <div className={`h-full rounded-r-full ${isDark ? 'bg-blue-400/60' : 'bg-blue-400/70'}`} style={{ width: `${d.diversity.gender.male}%` }} />
              </div>
              <div className="flex items-center gap-4 mt-1.5">
                <span className={`text-[8px] flex items-center gap-1 ${ts}`}><span className="w-2 h-2 rounded-sm bg-indigo-500" /> Female {d.diversity.gender.female}%</span>
                <span className={`text-[8px] flex items-center gap-1 ${ts}`}><span className={`w-2 h-2 rounded-sm ${isDark ? 'bg-blue-400/60' : 'bg-blue-400/70'}`} /> Male {d.diversity.gender.male}%</span>
                <span className={`text-[8px] flex items-center gap-1 ${ts}`}><span className="w-2 h-2 rounded-sm bg-emerald-500" /> Non-binary {d.diversity.gender.nonBinary}%</span>
              </div>
            </div>
            {/* Leadership gender */}
            <div className="mb-4">
              <p className={`text-[9px] mb-1.5 ${ts}`}>Leadership Gender Split</p>
              <div className="flex h-3 rounded-full overflow-hidden gap-[1px]">
                <div className="h-full bg-indigo-500/70 rounded-l-full" style={{ width: `${d.diversity.leadership.female}%` }} />
                <div className={`h-full rounded-r-full ${isDark ? 'bg-blue-400/40' : 'bg-blue-400/50'}`} style={{ width: `${d.diversity.leadership.male}%` }} />
              </div>
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-[8px] ${ts}`}>Female {d.diversity.leadership.female}%</span>
                <span className={`text-[8px] ${ts}`}>Male {d.diversity.leadership.male}%</span>
              </div>
            </div>
            {/* Tenure */}
            <p className={`text-[9px] mb-1.5 ${ts}`}>Tenure Distribution</p>
            <div className="space-y-1.5">
              {d.diversity.tenure.map(t => (
                <div key={t.range} className="flex items-center gap-2">
                  <span className={`text-[9px] w-16 ${tp}`}>{t.range}</span>
                  <div className="flex-1 h-2 rounded-full" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}>
                    <div className="h-full rounded-full bg-indigo-500/40 transition-all" style={{ width: `${t.pct}%` }} />
                  </div>
                  <span className={`text-[9px] w-8 text-right ${ts}`}>{t.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Compensation — compact grid */}
          <div className={`${cardBg} border ${border} rounded-xl p-4`} data-testid="compensation-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Scale className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
                <p className={`text-[11px] font-semibold ${tp}`}>Compensation Benchmarking</p>
              </div>
            </div>
            {/* Table header */}
            <div className={`grid grid-cols-[1.5fr_1fr_1.2fr_1.2fr_1fr] items-center px-2 py-1.5 mb-1 border-b ${border}`}>
              <span className={`text-[8px] font-semibold ${ts}`}>Band</span>
              <span className={`text-[8px] font-semibold text-center ${ts}`}>Count</span>
              <span className={`text-[8px] font-semibold text-center ${ts}`}>Median</span>
              <span className={`text-[8px] font-semibold text-center ${ts}`}>Market</span>
              <span className={`text-[8px] font-semibold text-right ${ts}`}>Delta</span>
            </div>
            <div className="space-y-0.5 mb-3">
              {d.compensation.bands.map(b => (
                <div key={b.band} className={`grid grid-cols-[1.5fr_1fr_1.2fr_1.2fr_1fr] items-center px-2 py-1.5 rounded-lg ${b.alert ? (isDark ? 'bg-red-500/5' : 'bg-red-50/30') : ''}`}>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] font-medium ${tp}`}>{b.band}</span>
                    {b.alert && <AlertTriangle className={`w-2.5 h-2.5 ${isDark ? 'text-red-400' : 'text-red-500'}`} />}
                  </div>
                  <span className={`text-[9px] text-center ${ts}`}>{b.employees}</span>
                  <span className={`text-[9px] text-center ${tp}`}>{b.medianSalary}</span>
                  <span className={`text-[9px] text-center ${ts}`}>{b.marketMedian}</span>
                  <div className="flex items-center justify-end gap-1">
                    <span className={`text-[9px] font-bold ${b.alert ? (isDark ? 'text-red-400' : 'text-red-500') : (isDark ? 'text-emerald-400' : 'text-emerald-600')}`}>{b.delta}</span>
                    {b.alert && (
                      <button onClick={() => handleCta('Review Compensation Band', b.band)} className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}>
                        Review
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className={`text-[9px] font-semibold mb-1.5 ${ts}`}>Pay Equity Gaps</p>
            {d.compensation.equityGaps.map((g, i) => (
              <div key={i} className="flex items-center justify-between px-2 py-1">
                <span className={`text-[9px] ${tp}`}>{g.category}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{g.gap}</span>
                  <span className={`text-[8px] ${g.improving ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{g.improving ? 'Improving' : 'Widening'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Learning & Development */}
        <div className={`${cardBg} border ${border} rounded-xl p-4`} data-testid="learning-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <GraduationCap className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-[11px] font-semibold ${tp}`}>Learning & Development</p>
              <span className={`text-[9px] ${ts}`}>{d.learning.coursesActive} courses active · {d.learning.certifications} certifications</span>
            </div>
            <button onClick={() => handleCta('Create Learning Path', 'Design new learning track for the team')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="orghealth-create-learning-btn">
              <BookOpen className="w-3 h-3" /> New Course
            </button>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {d.learning.topCourses.map((c, i) => (
              <div key={i} className={`px-3 py-2 rounded-lg border ${border}`}>
                <p className={`text-[10px] font-medium ${tp} truncate`}>{c.name}</p>
                <p className={`text-[9px] ${ts}`}>{c.enrolled} enrolled · {c.department}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                    <div className={`h-full rounded-full ${c.completion >= 80 ? 'bg-emerald-500/50' : 'bg-indigo-500/40'}`} style={{ width: `${c.completion}%` }} />
                  </div>
                  <span className={`text-[9px] font-bold ${c.completion >= 80 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : tp}`}>{c.completion}%</span>
                </div>
                <button onClick={() => handleCta('Enroll More', c.name)} className={`flex items-center gap-1 mt-1.5 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>
                  <ArrowRight className="w-3 h-3" /> Enroll more
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Turnover Predictions */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Brain className={`w-4 h-4 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
            <p className={`text-xs font-semibold ${tp}`}>Turnover Predictions</p>
            <span className={`text-[9px] ${ts}`}>{d.turnoverPredictions.length} at risk</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {d.turnoverPredictions.slice(0, 3).map(p => <TurnoverCard key={p.id} person={p} isDark={isDark} onAction={handleCta} />)}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            {d.turnoverPredictions.slice(3).map(p => <TurnoverCard key={p.id} person={p} isDark={isDark} onAction={handleCta} />)}
          </div>
        </div>

        {/* Dual Agent Insights — matching card structure */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src={AGENT_THUMBS.dora} alt="" className="w-4 h-4 rounded-full object-cover" />
              <p className={`text-xs font-semibold ${tp}`}>Dora's Analysis</p>
              <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>Data</span>
            </div>
            <div className="flex flex-col gap-2">
              {d.doraInsights.map(item => <DualInsightCard key={item.id} item={item} isDark={isDark} agent="Dora" agentKey="dora" />)}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src={AGENT_THUMBS.tali} alt="" className="w-4 h-4 rounded-full object-cover" />
              <p className={`text-xs font-semibold ${tp}`}>Tali's Context</p>
              <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>People</span>
            </div>
            <div className="flex flex-col gap-2">
              {d.taliInsights.map(item => <DualInsightCard key={item.id} item={item} isDark={isDark} agent="Tali" agentKey="tali" />)}
            </div>
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4PeopleOrgHealth;
